import React, {useEffect, useMemo, useState} from 'react';
import {Autocomplete, Badge, Box, Button, Checkbox, FormControlLabel, List, ListItem, TextField, Chip, Tooltip, Typography} from '@mui/material';
// import TableComponent from './TableComponent'; // TODO: can be deleted as soon as the new method is confirmed
import {Link} from 'react-router-dom';
import dynamicAudience from 'assets/icons/audiences/dynamic_aud.svg';
import staticAudience from 'assets/icons/audiences/static_aud.svg';
import {withStyles} from '@mui/styles';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import * as http from 'util/elemental_axios_client';
import * as elemental_axios_client from 'util/elemental_axios_client';
import {apiV1OrgUri} from 'constants/apiRoutes';
import {Loader} from 'components/index';
import {useImmer} from 'use-immer';
import MutationLoadingButton from 'components/MutationLoadingButton';
import * as PropTypes from 'prop-types';
import TooltipBigNumber from 'components/TooltipBigNumber';
import humanize from 'humanize-plus';

const dynamicIcon = <img src={dynamicAudience} alt='uploadIcon'/>;
const staticIcon = <img src={staticAudience} alt='uploadIcon'/>;

function MailIcon (props) {
    return null;
}

MailIcon.propTypes = {color: PropTypes.string};
// TODO: could be generalized into "multi value selector" .. not just for audiences
export const AudienceSelector = ({ 
    value, 
    onChange, 
    audiences,
    addSelectedButtonText = 'Add {num_selected} audience(s)' // Default text with {num_selected} as placeholder
}) => {
    const [ selectedAudiencesToInclude, setSelectedAudiencesToInclude ] = useState([]);
    const [ selectedAudiences, setSelectedAudiences ] = useImmer(value);
    const [ hasUserChanges, setHasUserChanges ] = useState(false);

    // Format the button text by replacing {num_selected} with the count
    const getButtonText = () => {
        return addSelectedButtonText.replace('{num_selected}', selectedAudiencesToInclude.length);
    };

    const onClickIncludeAudiences = () => {
        setSelectedAudiences( draft => { draft.push(...selectedAudiencesToInclude); });
        setSelectedAudiencesToInclude([]);
        setHasUserChanges(true);
    };

    const onClickRemoveAudience = ( audienceToRemove ) => {
        setSelectedAudiences( draft => {
            const index = draft.findIndex(audience => audience.id === audienceToRemove.id);
            if (index !== -1) {
                draft.splice(index, 1);
            }
        });
        setHasUserChanges(true);
    };

    useEffect( () => {
        if (onChange && hasUserChanges) {
            onChange(selectedAudiences);
        }
    }, [selectedAudiences]);

    const selectedAudienceIds = selectedAudiences.map(audience => audience.id);
    const availableAudiences = audiences.filter(audience => !selectedAudienceIds.includes(audience.id));
    const selectedAudiencesSorted = [...selectedAudiences].sort((a, b) => a.name.localeCompare(b.name));

    return (
        <>
            <Box display='flex' flexDirection='column' gap={2}>
                {availableAudiences?.length > 0 ? (
                    <>
                        <Box display='flex' gap={2} alignItems='flex-start'>
                            <Autocomplete
                                multiple
                                disableCloseOnSelect
                                id='multiple-limit-tags'
                                options={availableAudiences}
                                getOptionLabel={(audience) => audience.name}
                                value={selectedAudiencesToInclude}
                                style={{ flex: 1 }}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        fullWidth={true} 
                                        label='Choose audiences ...'
                                        size='small'
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { padding: '8px' }
                                        }}
                                        sx={{
                                            '& .MuiInputBase-root': {
                                                minHeight: '40px'
                                            },
                                            '& .MuiOutlinedInput-root': {
                                                padding: '4px'
                                            }
                                        }}
                                    />
                                )}
                                ListboxProps={{
                                    style: { maxHeight: '400px' }
                                }}
                                ListboxComponent={(props) => (
                                    <Box>
                                        <ul {...props} />
                                        <Box 
                                            sx={{ 
                                                position: 'sticky',
                                                bottom: 0,
                                                bgcolor: 'background.paper',
                                                borderTop: '1px solid',
                                                borderColor: 'divider',
                                                p: 1,
                                                display: 'flex',
                                                justifyContent: 'flex-end'
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    const autocompleteElement = document.getElementById('multiple-limit-tags');
                                                    if (autocompleteElement) {
                                                        autocompleteElement.blur();
                                                    }
                                                }}
                                            >
                                                Done
                                            </Button>
                                        </Box>
                                    </Box>
                                )}
                                renderOption={(props, option, { selected }) => (
                                    <li {...props}>
                                        {option.profiles_active_email_total && (
                                            <TooltipBigNumber 
                                                number={option.profiles_active_email_total}
                                                compact={true}
                                                chipProps={{
                                                    size: "small",
                                                    variant: "outlined",
                                                    sx: { 
                                                        height: '20px',
                                                        marginRight: 1,
                                                        '& .MuiChip-label': { 
                                                            px: 1,
                                                            fontSize: '0.75rem'
                                                        }
                                                    }
                                                }}
                                            />
                                        )}
                                        {option.type === 'dynamic' ? dynamicIcon : staticIcon}
                                        {option.name}
                                    </li>
                                )}
                                onChange={(e,value) => setSelectedAudiencesToInclude(value)}
                                size='small'
                            />

                            {selectedAudiencesToInclude.length > 0 && (
                                <Button
                                    className='new_primary_button'
                                    onClick={onClickIncludeAudiences}
                                    sx={{ 
                                        flexShrink: 0,
                                        whiteSpace: 'nowrap'
                                    }}
                                >
                                    {getButtonText()}
                                </Button>
                            )}
                        </Box>
                    </>
                ) : (
                    <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                            fontStyle: 'italic',
                            pl: 1 
                        }}
                    >
                        No more audiences available to select
                    </Typography>
                )}

                {selectedAudiencesSorted.length > 0 && (
                    <List>
                        {selectedAudiencesSorted.map((audience) => (
                            <ListItem key={audience.id}>
                                <Box display="flex" alignItems="center" gap={1} width="100%">
                                    {audience.profiles_active_email_total && (
                                        <TooltipBigNumber 
                                            number={audience.profiles_active_email_total}
                                            compact={true}
                                            chipProps={{
                                                size: "small",
                                                variant: "outlined",
                                                sx: { 
                                                    height: '20px',
                                                    '& .MuiChip-label': { 
                                                        px: 1,
                                                        fontSize: '0.75rem'
                                                    }
                                                }
                                            }}
                                        />
                                    )}
                                    {audience.type === 'dynamic' ? dynamicIcon : staticIcon}
                                    <Typography sx={{ flex: 1 }}>{audience.name}</Typography>
                                    <Button 
                                        onClick={() => { onClickRemoveAudience(audience);}}
                                        size="small"
                                        sx={{ 
                                            minWidth: 'auto',
                                            px: 1
                                        }}
                                    >
                                        remove
                                    </Button>
                                </Box>
                            </ListItem>
                        ))}
                    </List>
                )}
            </Box>
        </>
    );
};

